import React, { useRef, useState, useEffect, useCallback, use } from 'react';
import { FiCpu, FiEdit2, FiX } from 'react-icons/fi';
import { IoSparkles } from 'react-icons/io5';
import { RecruiterApi } from '../../../supports/recruiterApi';
import { ACTIVE_STATUSES, generateRequestId } from '../../../common/common';
import unifiedAuthManager from '../../../services/unifiedAuthManager';
import MultiSelectDropdown from '../../../common/multiSelectDropDown';
import SkillBadge from '../../../common/skillBadge';
import { useNavigate, useLocation, data } from 'react-router-dom';
import { useToast } from '../../../common/toastSystem';
import { X, Lightbulb, Sparkles, AlertCircle, RotateCcw,Filter,Code, Briefcase, MapPin, Globe, Languages, Building, Users, Award, Clock,Plus,Check } from 'lucide-react';
import { set } from 'es-toolkit/compat';
import { useFilter } from '../../../context/filterContext';

const prompts = [
  "Piping engineer with Navisworks skills",
  "Drafting engineer with AutoCAD expertise",
  "HVAC engineer with SAP skills"
];

const SkillTag = ({ children, onClick, disabled }) => (
  <button
    className={`flex items-center gap-2 mph-light mph-primary font-semibold text-xs px-3 py-1.5 rounded-full hover:shadow-lg ${disabled ? 'cursor-not-allowed' : ''}`}
    onClick={onClick}
    tabIndex={0}
    disabled={disabled}
  >
    <IoSparkles />
    {children}
  </button>
);

// Modal for showing missing fields and input
const PromptValidationModal = ({
  isOpen,
  missingFields,
  promptFields,
  onChangePromptText,
  onClose,
  onSearch,
  searching,
  errorMsg
}) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const isSparkleDisabled =
    searching ||
    !promptFields.promptText ||
    promptFields.promptText.trim().length === 0;
  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSparkleDisabled) onSearch(promptFields.promptText);
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl max-h-[56vh] rounded-xl shadow-xl flex flex-col relative">
        {/* Close button only - right aligned */}
        <div className="flex justify-end px-6 pt-1 border-gray-200">
          <button
            type="button"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => onClose(promptFields.promptText)}
            aria-label="Close"
            tabIndex={0}
          >
            <X size={18} className="text-gray-600" />
          </button>
        </div>

        <form
          className='overflow-y-auto px-6 py-2 flex flex-col gap-2'
          onSubmit={e => {
            e.preventDefault();
            if (!isSparkleDisabled) onSearch(promptFields.promptText);
          }}
        >
          {/* Concise Pro tip section */}
          {missingFields && missingFields.length > 0 && (
            <div className="flex items-start gap-3 p-1 bg-blue-50 rounded-lg">
              <Lightbulb className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5 " />
              <div className="flex-1">
                <p className="text-xs text-gray-700">
                  <span className="font-medium">Tip:</span> Include{' '}
                  <span className="text-blue-700 font-medium">
                    {missingFields.map(f =>
                      f.replace(/_/g, " ").toLowerCase()
                    ).join(', ')}
                  </span>
                  {' '}for better results
                </p>
              </div>
            </div>
          )}

          {/* Textarea section */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              id="prompt-textarea"
              className="w-full border border-gray-300 rounded-lg p-3 text-xs pr-12 resize-none focus:ring-0 focus:outline-none"
              rows={8}
              placeholder="Example: Mechanical Engineer with 6+ years of experience in HVAC design and AutoCAD. Proficient in project planning and quality control. Bachelor's degree in Mechanical Engineering required."
              value={promptFields.promptText || ''}
              onChange={e => onChangePromptText(e.target.value)}
              disabled={searching}
              maxLength={500}
              autoFocus
              onKeyDown={handleTextareaKeyDown}
            />

            {/* Clear button */}
            {!isSparkleDisabled && (
              <button
                type="button"
                className="absolute top-3 right-3 p-1 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                style={{ zIndex: 10 }}
                onClick={() => onChangePromptText('')}
                title='Clear'
                aria-label="Clear"
                tabIndex={0}
                disabled={searching}
              >
                <X size={16} />
              </button>
            )}

            {/* Character counter */}
            <div className="absolute bottom-3 right-4">
              <p className="text-xs text-gray-500">
                {promptFields.promptText?.length || 0}/500
              </p>
            </div>
          </div>


          <div className='flex justify-end'>
            {/* Search button */}
            <button
              type="submit"
              className={`border profiler-bg p-2 px-4 rounded-full mph-primary flex items-center justify-center transition-all`}
              // disabled={isSparkleDisabled}
              tabIndex={0}
              title='Search Candidates'
              aria-label="Search"
            >
              {searching ? (
                <svg className="animate-spin h-5 w-5 mph-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
              ) : (
                <p className='text-xs font-semibold'>
                  {promptFields.promptText?.trim()
                    ? "Search"
                    : "Search Anyway"}
                </p>
              )}
            </button>
          </div>

          {/* Error message */}
          {errorMsg && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{errorMsg}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const CandidateSearch = ({
  onCandidatesFound,
  setQuery,
  onTypingStateChange,
  sendFilterData
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialQuery = queryParams.get('query') || '';
  const initialSearchType = queryParams.get('type') || 'description';

  const [searchText, setSearchText] = useState(initialSearchType !== 'dropdown' ? initialQuery : '');
  const [selectedJob, setSelectedJob] = useState(initialSearchType === 'dropdown' ? initialQuery : '');
  const [searching, setSearching] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const jobsRequestRef = useRef(false);
  const searchRequestRef = useRef(false);
  const smartMatchRequestRef = useRef(false);
  const user = unifiedAuthManager.getUnifiedUser();
  const [isSearchDisabled, setIsSearchDisabled] = useState(searchText.length > 0 ? false : true);
  const [isTyping, setIsTyping] = useState(false);
  const [showPromptValidation, setShowPromptValidation] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [promptFields, setPromptFields] = useState({ promptText: '' });
  const [validatingPrompt, setValidatingPrompt] = useState(false);
  const [promptValidationError, setPromptValidationError] = useState(null);
  const [filterData, setFilterData] = useState(null);
  const [OgFilterData, setOgFilterData] = useState(null);
  const toast = useToast()
  const PAGE_SIZE = 50
  const [hasSearched, setHasSearched] = useState(false);
  const [currentFilters, setCurrentFilters] = useState([]); 
  const [addFilterVisible, setAddFilterVisible] = useState(false);  
  const [newFilterValue, setNewFilterValue] = useState('');
  const [hasFilterChanges, setHasFilterChanges] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState([]);
  const [showAddFilterDropdown, setShowAddFilterDropdown] = useState(false);
  const { currentFilter, setCurrentFilter } = useFilter();
  const { currentSearch, setCurrentSearch } = useFilter();
  const dropdownRef = useRef(null);
  const isRestoringRef = useRef(0);
  const [isRestoring, setIsRestoring] = useState(false);
  const floatRegex = /^\d*\.?\d*$/;
  // Reset modal state on searchText change

   useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAddFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMissingFields([]);
    setPromptFields({ promptText: '' });
    setShowPromptValidation(false);
    setPromptValidationError(null);
  }, [searchText]);

useEffect(() => {
  console.log("Syncing from context:", currentFilter, currentSearch);
  
  // Only restore once when component mounts and context has data
  if (isRestoringRef.current === 0 && (currentFilter !== null || currentSearch !== null)) {
    isRestoringRef.current = true;
    setFilterData(currentFilter);
    setOgFilterData(currentFilter);
    setSearchText(currentSearch);
    setIsRestoring(true); // ✅ Set flag to trigger search
  }
}, [currentFilter, currentSearch]);

// Second useEffect - trigger search after filterData is updated
useEffect(() => {
if (isRestoring && Array.isArray(filterData) && filterData.length > 0) {
    handleFinalSearch(searchText?.trim());
    setIsRestoring(false);
  }
}, [isRestoring, filterData]);

 const handleSelectNewFilter = (filter) => {
    const newFilter = {
      id: filter.id,
      label: filter.label,
      type: filter.type,
      value: filter.type === 'experience' ? {} : []
    };
     setHasFilterChanges(false);
    setFilterData(prev => [...prev, newFilter]);
    setShowAddFilterDropdown(false);
    
    // Auto-open the add input for the new filter
    setTimeout(() => {
      handleAddFilter(filter.label);
    }, 100);
  };

  useEffect(() => {
  const hasFilterChanged = () => {
    if (!filterData || !OgFilterData) return false;
    
    // Check if lengths are different
    if (filterData.length !== OgFilterData.length) return true;
    
    // Deep compare each filter item
    for (let i = 0; i < filterData.length; i++) {
      const current = filterData[i];
      const original = OgFilterData.find(f => f.id === current.id);
      
      if (!original) return true;
      
      // Compare array values
      if (Array.isArray(current.value) && Array.isArray(original.value)) {
        if (current.value.length !== original.value.length) return true;
        
        // Check if all values match
        const sortedCurrent = [...current.value].sort();
        const sortedOriginal = [...original.value].sort();
        
        if (!sortedCurrent.every((val, idx) => val === sortedOriginal[idx])) {
          return true;
        }
      }
      // Compare object values (like experience)
      else if (typeof current.value === 'object' && typeof original.value === 'object') {
        if (JSON.stringify(current.value) !== JSON.stringify(original.value)) {
          return true;
        }
      }
      // Compare if one is array and other is object
      else if (typeof current.value !== typeof original.value) {
        return true;
      }
    }
    
    return false;
  };
  
  setHasFilterChanges(hasFilterChanged());
}, [filterData, OgFilterData]);

  useEffect(() => {
    if (typeof setQuery === 'function') {
      setQuery(searchText);
    }
  }, [searchText, setQuery]);

  useEffect(() => {
    if (searchText === '' && selectedJob === '') {
      notifyCandidatesFound('', false, '', '');
      setHasSearched(false);
    }
    if (!initialQuery) return;
    const controller = new AbortController();
    const signal = controller.signal;
    if (searchText) {
      let obj = { title: searchText };
      notifyCandidatesFound('', true, 'description', obj);
      handleFinalSearch(initialQuery);
    }
  }, []);

  const handleApplyFilters = async () => {
  if (!hasFilterChanges) return;
  try {
    setOgFilterData(JSON.parse(JSON.stringify(filterData)));
    setHasFilterChanges(false);
    
    // Optionally trigger a new search with updated filters
    handleFinalSearch('');
    setAddFilterVisible(false);
    
  } catch (error) {
    console.log("Error applying filters:", error);
    toast.error("Failed to apply filters");
  }
};

  useEffect(() => {
    if (jobsRequestRef.current) return;
    jobsRequestRef.current = true;
    const recruiterId = user?.id;
    let isMounted = true;
    const fetchJobs = async () => {
      setJobsLoading(true);
      setJobsError(null);
      try {
        const response = await RecruiterApi.getJobsListOnStatus({
          "recruiter_id": recruiterId,
          user_payload: { request_id: generateRequestId() },
          status: ACTIVE_STATUSES
        });
        setJobsLoading(false)
        if (response && response.success && Array.isArray(response.data)) {
          setJobs(response.data);
          if (selectedJob) {
            const match = selectedJob.match(/\(([^)]+)\)$/);
            let mph_jobid = match ? match[1] : selectedJob;
            const jobObj = response.data.find(
              j =>
                String(j.mph_jobid) === mph_jobid ||
                String(j.id) === selectedJob ||
                `${j.title} (${j.mph_jobid})` === selectedJob
            );
            if (searching || smartMatchRequestRef.current) return;
            handleSmartMatchForJob(jobObj, selectedJob);
          }
        } else if (isMounted) {
          setJobs([]);
        }
      } catch (err) {
        if (isMounted) {
          setJobsError('Failed to load jobs');
          setJobs([]);
        }
      } finally {
        if (isMounted) setJobsLoading(false);
      }
    };
    fetchJobs();
    return () => { isMounted = false; };
  }, []);


  const getIcon = (name, size = 24, color = "currentColor") => {
 const icons = {
    Experience: Clock,
    Location: MapPin,
    Nationality: Globe,
    Sector: Users,
    "Job Title": Briefcase,
    Title: Briefcase,
    Qualification: Award,
    Subsector: Building,
    Languages: Languages,
    Skill: Code
  };
  
  const IconComponent = icons[name];
  
  if (!IconComponent) {
    return null;
  }
  
  return <IconComponent size={size} color={color} />;
};

  const notifyCandidatesFound = (candidates, loading, searchType, query) => {
    if (onCandidatesFound) {
      onCandidatesFound({ candidates, loading, searchType, query });
    }
  };

  const validatePrompt = async (prompt) => {
    setValidatingPrompt(true);
    setPromptValidationError(null);
      setOgFilterData(null);
      setFilterData(null);
    try {
      const requestBody = {
        user_payload: { request_id: generateRequestId(), email: user?.email, user_id: parseInt(user?.id), tenant: 'MPH' },
        search_prompt: prompt
      }
      const response = await RecruiterApi.validatePromptSearch(requestBody);
      if (!response) {
        toast.warning("Please try again later.")
        setValidatingPrompt(false);
        return
      }
      if (response?.is_valid) {
        setShowPromptValidation(false);
        setMissingFields([]);
        setPromptFields({ promptText: prompt });
        setPromptValidationError(null);
        handleFinalSearch(prompt);
      } else {
        const missing = Array.isArray(response?.missing) ? response?.missing : [];
        setMissingFields(missing);
        if (missing.length > 0) {
          setShowPromptValidation(true);
          setPromptFields({ promptText: prompt });
          setPromptValidationError(null);
        } else {
          setShowPromptValidation(false);
          setSearchText(prompt); // Sync
          handleFinalSearch(prompt);
        }
      }
    } catch (err) {
      setPromptValidationError(
        "Unable to check prompt. Please try again."
      );
      setShowPromptValidation(true);
      setPromptFields({ promptText: prompt });
      setSearchText(prompt); // Sync
    } finally {
      setValidatingPrompt(false);
    }
  };





  const handleFinalSearch = useCallback(async (finalPrompt, pageNum = 0,) => {
    if (!finalPrompt && !filterData) return;
    if (searchRequestRef.current) return;
    searchRequestRef.current = true;
    setSearching(true);
    if(finalPrompt){
       setSearchText(finalPrompt);
    } 
   // Sync modal updates to main field always!
    notifyCandidatesFound('', true, 'description', { title: finalPrompt });
    try {
      console.log("Final Search Triggered with:", finalPrompt, filterData);
    
      const response = await RecruiterApi.searchCandidates({
        page: pageNum,
        count: PAGE_SIZE,
        user_payload: { request_id: generateRequestId(), email: user?.email, user_id: user?.id },
       search_text: filterData && filterData.some(filter => filter.value && filter.value.length > 0) 
  ? finalPrompt 
  : searchText,
        active_filters: finalPrompt? [] : filterData 
          });
          if (response && response.success ) {
            setOgFilterData(response.data.active_filters || null);
            setFilterData(response.data.active_filters || null);
            sendFilterData(response.data.active_filters || null);
            setCurrentFilter(response.data.active_filters || null);
            setCurrentSearch(searchText)
                console.log("Candidate Search Response:", response.data.active_filters);
          }
  
      // navigate(`?query=${encodeURIComponent(finalPrompt)}&type=description`, { replace: true });
      setSearching(false);
      if (response && response.success && Array.isArray(response.data?.candidates)) {
        const metadataList = response.data.candidates;
        notifyCandidatesFound(metadataList, false, 'description', { title: finalPrompt, total_count: response.data.total_count || 0 });
        setHasSearched(true);
      } else {
        notifyCandidatesFound([], false, 'description', { title: finalPrompt, total_count: 0 });
      }
    } catch (err) {
      notifyCandidatesFound([], false, 'description', { title: finalPrompt });
    } finally {
      setSearching(false);
      searchRequestRef.current = false;
      setShowPromptValidation(false);
    }
  }, [user, notifyCandidatesFound, navigate]);

  const handleModalSearch = (modalPromptOverride) => {

    console.log("Modal Search Triggered with:", modalPromptOverride);
    if (searching || validatingPrompt) return;
    const promptTextValue =
      typeof modalPromptOverride === 'string'
        ? modalPromptOverride.trim()
        : (promptFields.promptText ? promptFields.promptText.trim() : '');

    if (!promptTextValue) {
      setPromptValidationError("Please enter your search prompt with added detail.");
      return;
    }
    setSearchText(promptTextValue);
    setShowPromptValidation(false); // close the modal right on click
    setPromptValidationError(null);
    handleFinalSearch(promptTextValue);
  };

  // Prevent multiple smart match calls
  const handleSmartMatchForJob = async (jobObj, searchedJob) => {
    if (!jobObj) return;
    if (smartMatchRequestRef.current) return;
    smartMatchRequestRef.current = true;
    setSearching(true);
    notifyCandidatesFound('', true, 'dropdown', jobObj);
    try {
        setFilterData([]);
        setOgFilterData([]);
        sendFilterData([]);
        setCurrentFilter([]);
      const response = await RecruiterApi.getSmartMatchCandidates({
        user_payload: {
          request_id: generateRequestId(),
          email: user?.email,
          tenant: "",
          user_id: user?.id
        },
        payload: {
          job_id: jobObj.id || jobObj.mph_jobid
        }
      });
      // navigate(`?query=${encodeURIComponent(searchedJob)}&type=dropdown`, { replace: true });
      if (response && response.success && Array.isArray(response.data?.hits)) {
        const metadataList = response.data.hits;
        notifyCandidatesFound(metadataList, false, 'dropdown', jobObj);
        setHasSearched(true);
      } else {
        notifyCandidatesFound([], false, 'dropdown', jobObj);
      }
    } catch (err) {
      notifyCandidatesFound([], false, 'dropdown', jobObj);
    } finally {
      setSearching(false);
      smartMatchRequestRef.current = false;
    }
  };

  // Handle Enter or button click search, but add prompt validation step
const handleKeyDown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    if (!searching && !searchRequestRef.current && !validatingPrompt) {
      const currentText = searchText.trim();
      setSearchText(currentText);
      setSelectedJob('');
      setIsTyping(false);
      if (onTypingStateChange) {
        onTypingStateChange(false);
      }
      
      // Direct search without validation modal
      if (currentText) {
        handleFinalSearch(currentText);
      }
    }
  }
};

const handleAddFilter = (filter) => {
  try {
    setCurrentFilters(filter);
    setAddFilterVisible(true);
    
    // Find the filter item to check its type
    const filterItem = filterData.find(item => item.label === filter);
    
    // Initialize as object for experience, empty string for others
    if (filterItem?.type === 'experience') {
      setNewFilterValue({ min: '', max: '' });
    } else {
      setNewFilterValue('');
    }
  } catch (error) {
    console.log("Error adding filter:", error);
  }
};

const handlecancelFilter = () => {
  try {
    setCurrentFilters('');
    setAddFilterVisible(false);
    setNewFilterValue('');
  } catch (error) {
    console.log("Error canceling filter:", error);
  }
};

  const handlePromptClick = (prompt) => {
    if (searching || searchRequestRef.current || validatingPrompt) return;
    setSearchText(prompt);
    setSelectedJob('');
    setIsTyping(false);
    if (onTypingStateChange) {
      onTypingStateChange(false);
    }
    validatePrompt(prompt);
  };

  const handleJobChange = (e) => {
    let value = e?.target?.value ?? '';
    setSelectedJob(value);
    setSearchText('');
    setCurrentSearch('');
    setIsTyping(false);
    if (onTypingStateChange) {
      onTypingStateChange(false);
    }
    if (value) {
      // Create jobObj with title, mph_jobid, and id extracted from selected value
      const match = value.match(/(.+?)\s*\(([^)]+)\)$/);
      let title, mph_jobid, id;
      if (match) {
        title = match[1]?.trim();
        mph_jobid = match[2];
        // Try to find id from jobs array
        const foundJob = jobs.find(
          j =>
            String(j.mph_jobid) === mph_jobid ||
            `${j.title} (${j.mph_jobid})` === value
        );
        id = foundJob ? foundJob.id : undefined;
      } else {
        mph_jobid = value;
        // Try to find job in jobs with matching mph_jobid or id
        const foundJob = jobs.find(j =>
          String(j.mph_jobid) === mph_jobid || String(j.id) === value
        );
        title = foundJob ? foundJob.title : '';
        id = foundJob ? foundJob.id : undefined;
      }
      const jobObj = { title, mph_jobid, id };
      if (searching || smartMatchRequestRef.current) return;
      handleSmartMatchForJob(jobObj, value);
    }
  };

  // When user clicks search (sparkle button)
  const handleSearchClick = () => {
 
    if (searching || searchRequestRef.current || validatingPrompt) return;
    const currentText = searchText.trim();
    setSearchText(currentText);
    setIsTyping(false);
    if (onTypingStateChange) {
      onTypingStateChange(false);
    }
    if (!currentText) return;
    validatePrompt(currentText);
  };

  const handleClearSearchText = () => {
      isRestoringRef.current = false; 
       setFilterData([])
    setOgFilterData([])
    sendFilterData([])
    setCurrentFilter([])
    setSearchText('');
    setCurrentSearch('');
    setIsSearchDisabled(true);
    setIsTyping(false);
    setShowPromptValidation(false);
    setMissingFields([]);
    setPromptFields({ promptText: '' });
    setPromptValidationError(null);
    setHasSearched(false);
    if (onTypingStateChange) {
      onTypingStateChange(false);
    }
    notifyCandidatesFound('', false, '', '');
    navigate(`?`, { replace: true });
  };

  const handleClearSelectedJob = () => {
    setSelectedJob('');
    setIsTyping(false);
    setHasSearched(false);
    if (onTypingStateChange) {
      onTypingStateChange(false);
    }
    notifyCandidatesFound('', false, '', '');
    navigate(`?`, { replace: true });
  };

  const getSelectedJobLabel = () => {
    if (!selectedJob) return '';
    const jobObj = jobs.find(job => job.mph_jobid === selectedJob);
    if (jobObj) {
      return jobObj.title ? `${jobObj.title} (${jobObj.mph_jobid})` : jobObj.mph_jobid;
    }
    return selectedJob;
  };

  const searchTextMaxLength = 500;

  const handleTextareaChange = (e) => {
    let text = e.target.value;
    if (text.length > searchTextMaxLength) {
      text = text.slice(0, searchTextMaxLength);
    }
    setSearchText(text);
    if (text && isSearchDisabled) {
      setIsSearchDisabled(false);
    } else if (!text && !isSearchDisabled) {
      setIsSearchDisabled(true);
    }
    setIsTyping(text.length > 0);
    if (onTypingStateChange) {
      onTypingStateChange(text.length > 0);
    }
  };

  const handleModalPromptTextChange = (value) => {
    setPromptFields({ promptText: value });
    setPromptValidationError(null);
  };

  const handleClosePromptModal = (modalPromptValueFromModal) => {
    const updatedPrompt =
      typeof modalPromptValueFromModal === 'string'
        ? modalPromptValueFromModal
        : promptFields.promptText;

    setShowPromptValidation(false);
    setPromptValidationError(null);
    setSearchText(updatedPrompt || "");
  }


const handleConfirmFilter = () => {
  const updatedFilterData = filterData.map((item) => {
    if (item.label === currentFilters) {
      // Handle experience type filters
      if (item.type === 'experience') {
        const min = newFilterValue.min ? parseFloat(newFilterValue.min) : null;
        const max = newFilterValue.max ? parseFloat(newFilterValue.max) : null;

        // Validation: at least one value should be provided
        if (!min && !max) {
          toast.warning('Please enter at least minimum or maximum experience');
          return item;
        }
        
        // Validation: min should not be greater than max
        if (min && max && min > max) {
          toast.warning('Minimum experience cannot be greater than maximum');
          return item;
        }
        
        // Build the experience value object with gte and lte
        let experienceValue = {};
        if (min !== null) experienceValue.gte = min;
        if (max !== null) experienceValue.lte = max;
        
        return {
          ...item,
          value: experienceValue
        };
      }
      
      // Handle array-based filters (Languages, Title, etc.)
      if (Array.isArray(item.value)) {
        if (!newFilterValue.trim()) {
          toast.warning('Please enter a value');
          return item;
        }
        if (item.value.includes(newFilterValue.trim())) {
          toast.warning(`${newFilterValue.trim()} already exists in ${item.label}`);
          return item;
        }
        return {
          ...item,
          value: [...item.value, newFilterValue.trim()]
        };
      }
      return item;
    }
    return item;
  });

  setFilterData(updatedFilterData);
  handlecancelFilter();
};
const toggleFilterExpansion = (filterLabel) => {
  setExpandedFilters(prev => 
    prev.includes(filterLabel) 
      ? prev.filter(f => f !== filterLabel)
      : [...prev, filterLabel]
  );
};

const handleCancelFilters = () => {
  setFilterData(OgFilterData ? JSON.parse(JSON.stringify(OgFilterData)) : null);
  setHasFilterChanges(false);
  setAddFilterVisible(false);
  setCurrentFilters('');
  setNewFilterValue('');
  setFilterData(null)
  setOgFilterData(null)
   sendFilterData([])
   setCurrentFilter([])
   handleClearSearchText()
};

// Handler to remove a filter value
const handleRemoveFilter = (label, valueToRemove) => {
  setFilterData(prev =>
    prev.map(item => {
      if (item.label !== label) return item;

      // ARRAY TYPE FILTER
      if (Array.isArray(item.value)) {
        const updatedValues = item.value.filter(
          val => val !== valueToRemove
        );

        return {
          ...item,
          value: updatedValues, // 👈 KEEP EMPTY ARRAY
        };
      }

      // RANGE TYPE FILTER
      return {
        ...item,
        value: {}, // or null depending on your design
      };
    })
  );
};

const AVAILABLE_FILTERS = [
  { id: 'experience', label: 'Experience', type: 'experience' },
  { id: 'location', label: 'Location', type: 'array' },
  { id: 'nationality', label: 'Nationality', type: 'array' },
  { id: 'sector', label: 'Sector', type: 'array' },
  { id: 'title', label: 'Title', type: 'array' },
  { id: 'qualification', label: 'Qualification', type: 'array' },
  // { id: 'subsector', label: 'Subsector', type: 'array' },
  { id: 'languages', label: 'Languages', type: 'array' },
  { id: 'skill', label: 'Skill', type: 'array' }
];

const getAvailableFiltersToAdd = () => {
  if (!filterData) return AVAILABLE_FILTERS;
    const existingFilterIds = filterData.map(f => f.label);
    return AVAILABLE_FILTERS.filter(f => !existingFilterIds.includes(f.label));
  };

  const availableFiltersToAdd = getAvailableFiltersToAdd();
  

  return (
    <div className="text-left bg-white p-6 rounded-2xl flex flex-col gap-3 min-h-[calc(100vh-9rem)]">
      <PromptValidationModal
        isOpen={showPromptValidation}
        missingFields={missingFields}
        promptFields={promptFields}
        onChangePromptText={handleModalPromptTextChange}
        onClose={handleClosePromptModal}
        onSearch={handleModalSearch}
        searching={searching || validatingPrompt}
        errorMsg={promptValidationError}
      />
      <div className="flex items-center gap-3">
        <FiCpu className="h-6 w-6 mph-primary" />
        <div className="text-lg font-bold text-gray-333">AI powered candidate search</div>
      </div>
      <p className="text-xs text-gray-999">Let our AI find your perfect candidates with intelligent matching</p>

      <div>
        <label className="flex items-center gap-2 text-sm mph-primary relative group">
          <FiEdit2 /> Describe the role or skills you're looking for
          {/* 💡 Bulb icon with hover tooltip */}
          <div className="relative inline-block">
            <span className="text-yellow-500 text-sm cursor-pointer">💡</span>
            {/* Tooltip box above the bulb */}
            <div className="absolute hidden group-hover:flex bottom-full left-1/2 -translate-x-[90%] mb-2 w-72 mph-light mph-primary rounded-md p-2 text-xs shadow-md z-10">
              Provide details such as the Job Title, Core Skills,
              Key Responsibilities, Years of Experience, and
              Educational Qualification.
            </div>
            {/* Small arrow pointing to the bulb */}
            <div className="absolute hidden group-hover:block bottom-[calc(100%-2px)] left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-amber-200"></div>
          </div>
        </label>
        <div className="relative">
          <textarea
            value={searchText}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Eg: Mechanical Engineer with 6 years of experience in HVAC design and maintenance. Proficient in AutoCAD, project planning, and quality control. Holds a B.E. in Mechanical Engineering."
            className="w-full mt-2 p-3 text-gray-333 border border-gray-200 rounded-lg text-xs placeholder:text-gray-999 resize-none"
            style={{ minHeight: '100px', maxHeight: 'calc(100vh - 35rem)', overflowY: 'auto' }}
            spellCheck={true}
            disabled={searching || showPromptValidation || validatingPrompt}
            aria-label="Describe the role or skills you're looking for"
            maxLength={searchTextMaxLength}
            autoComplete="off"
          />
          <div
            onClick={!searching && !validatingPrompt && !showPromptValidation ? handleSearchClick : undefined}
            className={`absolute right-1 bottom-3 flex items-center select-none ${isSearchDisabled || searching || validatingPrompt ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            {(searching || validatingPrompt) ? (
              <svg className="animate-spin h-5 w-5 mph-primary mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            ) : (
              <IoSparkles className="mph-primary mr-1" title='Candidate Search' size={18} />
            )}
          </div>
          {/* Show close icon if searchText is not empty */}
          {(isTyping || searchText) && (
            <button
              type="button"
              className="absolute top-3 right-2 p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500"
              style={{ zIndex: 10 }}
              onClick={handleClearSearchText}
              aria-label="Clear search"
              tabIndex={0}
              disabled={searching || validatingPrompt}
            >
              <FiX size={16} />
            </button>
          )}

            {searchText === '' && hasSearched && (
          <div className="absolute bottom-2 right-10">
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-700 underline"
              onClick={() => {
                notifyCandidatesFound('', false, '', '');
                setHasSearched(false);
              }}
              aria-label="Clear search results"
              title='Clear search results'
              tabIndex={0}
            >
              <RotateCcw size={14} />
            </button>
          </div>
        )}
        </div>
      
      </div>

      {/* <div className="flex flex-wrap gap-2 border-b pb-4">
        {prompts.map((prompt, idx) => (
          <SkillTag
            key={prompt}
            onClick={() => handlePromptClick(prompt)}
            disabled={searching || showPromptValidation || validatingPrompt}
          >
            {prompt}
          </SkillTag>
        ))}
      </div> */}
{filterData && Array.isArray(filterData) && filterData.length > 0 && (
  <div className='w-full flex flex-col p-2 border-t pt-3 border-b pb-4'>
    <div className="flex items-center gap-2 mb-3">
      <Filter width="0.9em" height='0.9em' />
      <label className="text-sm font-semibold text-gray-800">Filters</label>
    </div>

    {filterData.map((dataItem, index) => {
      const isArray = Array.isArray(dataItem.value);
      const hasValues = isArray && dataItem.value.length > 0;
      const isEditingThis =
        addFilterVisible && currentFilters === dataItem.label;

      return (
        <div key={dataItem.id || index} className="flex pb-3 flex-col gap-4">
          <div className='w-fit'>
            {/* LABEL */}
            <div className="flex items-center gap-1.5 mb-2">
              {getIcon(dataItem.label, 14, "#004645")}
              <label className="text-xs text-gray-600">{dataItem.label}</label>
            </div>

            {/* VALUES + ADD BUTTON */}
            <div className='flex flex-wrap gap-2'>
              {isArray ? (
                <>
                  {/* EMPTY STATE */}
                  {!hasValues && (
                    <div className='text-[0.65rem] flex items-center text-gray-400 italic'>
                      {dataItem.label}
                    </div>
                  )}

                  {/* FIRST VALUE */}
                  {hasValues && (
                    <div className='flex items-center justify-center rounded-2xl gap-2 py-1 px-2 bg-green-100'>
                      <div className='text-[0.65rem] text-gray-600 font-bold'>
                        {dataItem.value[0]}
                      </div>
                      <X
                        className='h-3 w-3 text-gray-600 cursor-pointer'
                        onClick={() =>
                          handleRemoveFilter(dataItem.label, dataItem.value[0])
                        }
                      />
                    </div>
                  )}

                  {/* +N MORE */}
                  {hasValues &&
                    dataItem.value.length > 3 &&
                    !expandedFilters.includes(dataItem.label) && (
                      <div
                        className='flex items-center justify-center rounded-2xl gap-2 py-1 px-2 bg-blue-100 cursor-pointer hover:bg-blue-200'
                        onClick={() => toggleFilterExpansion(dataItem.label)}
                      >
                        <div className='text-[0.65rem] text-blue-600 font-bold'>
                          +{dataItem.value.length - 1} more
                        </div>
                      </div>
                    )}

                  {/* REMAINING VALUES */}
                  {hasValues &&
                    (dataItem.value.length <= 3 ||
                      expandedFilters.includes(dataItem.label)) &&
                    dataItem.value.slice(1).map((value, valueIndex) => (
                      <div
                        key={valueIndex}
                        className='flex items-center justify-center rounded-2xl gap-2 py-1 px-2 bg-green-100'
                      >
                        <div className='text-[0.65rem] text-gray-600 font-bold'>
                          {value}
                        </div>
                        <X
                          className='h-3 w-3 text-gray-600 cursor-pointer'
                          onClick={() =>
                            handleRemoveFilter(dataItem.label, value)
                          }
                        />
                      </div>
                    ))}

                  {/* SHOW LESS */}
                  {hasValues &&
                    dataItem.value.length > 3 &&
                    expandedFilters.includes(dataItem.label) && (
                      <div
                        className='flex items-center justify-center rounded-2xl gap-2 py-1 px-2 bg-blue-100 cursor-pointer hover:bg-blue-200'
                        onClick={() => toggleFilterExpansion(dataItem.label)}
                      >
                        <div className='text-[0.65rem] text-blue-600 font-bold'>
                          Show less
                        </div>
                      </div>
                    )}
                </>
              ) : (
                /* RANGE TYPE */
                 dataItem.value?.gte || dataItem.value?.lte?(
                <div className='flex items-center justify-center rounded-2xl gap-2 py-1 px-2 bg-green-100'>
                 
                  <div className='text-[0.65rem] text-gray-600 font-bold'>
                    {dataItem.value?.gte && dataItem.value?.lte
                      ? `${dataItem.value.gte} - ${dataItem.value.lte}`
                      : dataItem.value?.gte
                      ? `${dataItem.value.gte}+`
                      : dataItem.value?.lte
                        ? `≥ ${dataItem.value.lte}`
                        : dataItem.label
                        }
                  </div>
                
                  <X
                    className='h-3 w-3 text-gray-600 cursor-pointer'
                    onClick={() => handleRemoveFilter(dataItem.label, null)}
                  />
                  
                </div>
                  ):(
                    <div className='text-[0.65rem] flex items-center text-gray-400 italic'>
                      {dataItem.label}
                    </div>)
              )}

              {/* ADD BUTTON — FIXED */}
              {(!isEditingThis || !hasValues) && (
                <div
                  onClick={() => handleAddFilter(dataItem.label)}
                  className='flex items-center justify-center rounded-full gap-2 py-1 px-2 hover:bg-green-200 cursor-pointer'
                >
                  {dataItem.type === 'experience' ? (
                    <FiEdit2 className='text-[#004645] h-3 w-3' />
                  ) : (
                    <Plus className='text-[#004645] h-3 w-4' />
                  )}
                </div>
              )}
            </div>

            {/* ADD FILTER INPUT */}
            {isEditingThis && (
                      <div className='mt-2 flex items-center gap-2'>
              {dataItem.type === 'experience' ? (
                <>
                            <input
              type="text"
              inputMode="decimal"
              placeholder="Min years"
              autoFocus
              value={newFilterValue.min || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || floatRegex.test(value)) {
                  setNewFilterValue({
                    ...newFilterValue,
                    min: value,
                  });
                }
              }}
              className="w-full border border-gray-300 rounded-lg p-2 text-xs"
            />

                  <span className='text-xs text-gray-500'>to</span>
                  <input
                   type="text"
                    inputMode="decimal"
                    placeholder='Max years'
                    value={newFilterValue.max || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only accept non-negative numbers (integers and floats)
                      if (value === '' || floatRegex.test(value)) {
                        setNewFilterValue({
                          ...newFilterValue,
                          max: value,
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent minus, plus, and 'e'
                      if (['-', '+', 'e', 'E'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className='w-full border border-gray-300 rounded-lg p-2 text-xs'
                  />
                </>
              ) : (
                <input
                  type='text'
                  autoFocus
                  placeholder={`Add ${dataItem.label.toLowerCase()}`}
                  value={newFilterValue}
                  onChange={(e) => setNewFilterValue(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && handleConfirmFilter()
                  }
                  className='w-full border border-gray-300 rounded-lg p-2 text-xs'
                />
              )}

              <button
                title='Cancel'
                onClick={handlecancelFilter}
                className=' text-red-500 hover:bg-gray-50 border p-1.5 rounded-full'
              >
                <X className='h-4 w-4' />
              </button>

              <button
                title='Confirm'
                onClick={handleConfirmFilter}
                className=' text-green-500 hover:bg-gray-50 border p-1.5 rounded-full'
              >
                <Check className='h-4 w-4' />
              </button>
            </div>
            )}
          </div>
        </div>
      );
    })}
    <div ref={dropdownRef} onClick={() => setShowAddFilterDropdown(!showAddFilterDropdown)} className=' mt-2 relative items-center gap-1 py-1 px-3 text-gray-600 cursor-pointer hover:bg-gray-50 rounded-full border w-fit font-semibold flex text-[0.65rem]'> 
      <Plus className='text-[#004645] h-3 w-3' /> Add More Filters
       {showAddFilterDropdown && (
                <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <div className="py-1">
                    {availableFiltersToAdd.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => handleSelectNewFilter(filter)}
                        className="w-full text-left px-4 py-2 text-xs hover:bg-gray-100 flex items-center gap-2"
                      >
                        {getIcon(filter.label, 14, "#004645")} 
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
      </div>

    {/* FOOTER */}
    <div className='w-full gap-2 flex justify-end'>
      <div
        onClick={handleCancelFilters}
        className='text-xs border text-gray-500 hover:text-gray-700 px-2 py-1 rounded-2xl cursor-pointer'
      >
        Reset
      </div>

      <div
        className={`text-xs px-3 py-1 rounded-2xl ${
          hasFilterChanges
            ? 'text-white bg-[#B2853D] hover:bg-[#9a7033] cursor-pointer'
            : 'text-white bg-[#B2853D] opacity-70 cursor-not-allowed'
        }`}
        onClick={hasFilterChanges ? handleApplyFilters : undefined}
      >
        Apply
      </div>
    </div>
  </div>
)}

      <div>
        <label className="text-xs font-semibold text-gray-333">Or select a job</label>
        <div className="flex items-center gap-2">
          <MultiSelectDropdown
            label=""
            name="job"
            options={
              jobs.map(job => ({
                label: job.title ? `${job.title} (${job.mph_jobid})` : job.mph_jobid,
                value: job.mph_jobid
              }))
            }
            selected={
              selectedJob
                ? [
                  jobs.find(job => job.mph_jobid === selectedJob)?.title
                    ? `${jobs.find(job => job.mph_jobid === selectedJob).title} (${selectedJob})`
                    : selectedJob
                ]
                : []
            }
            onChange={vals => {
              handleJobChange(vals);
            }}
            isMultiSelect={false}
            isLoading={jobsLoading}
            placeholder={jobsError ? jobsError : "Search by ID or Name"}
            disabled={searching || showPromptValidation || validatingPrompt}
            className="w-full font-semibold text-xs text-gray-333"
          />
        </div>
        {selectedJob && (
          <div className="mt-4 flex items-center gap-2">
            <SkillBadge>
              {getSelectedJobLabel()}
            </SkillBadge>
            <button
              type="button"
              className="ml-1 p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500"
              onClick={handleClearSelectedJob}
              aria-label="Clear selected job"
              tabIndex={0}
              disabled={searching}
              style={{ height: 24, width: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <FiX size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateSearch;